import gql from 'graphql-tag';
import { getLinkData } from '@/core/utils';
import { IconGridFragment } from '@graphqlTypes';
import { Container, SectionFooter, SectionHeader, RevealWrapper, Image, RhLink } from '@components';

import { useRef } from "react";
import { useInView } from "motion/react";
import { useViewSize } from "@/providers/ViewSizeContext";
import { AnimVals } from '@/utils/animUtils';

import styles from './IconGrid.module.scss';
import className from 'classnames/bind';
const cx = className.bind(styles);

type IconGridItem = IconGridFragment['items'][number];

const IconGridItem = (props: IconGridItem) => {
  const { icon, headline, body, cta } = props;
  const ctaData = getLinkData(cta);
  return (
    <div className={cx(["icon-grid-item"])}>
      { icon && (
        <div className={cx(["icon"])}>
          <Image image={icon} />
        </div>
      )}
      { headline && <h3 className={cx(["headline", "title-lc-s"])}>{headline}</h3> }
      { body && <p className={cx(["body", "text-m"])}>{body}</p> }
      {ctaData && (
        <RhLink href={ctaData.url} target={ctaData.target} className={cx(["cta", "inline-link"])}>
          {ctaData.label}
        </RhLink>
      )}
    </div>
  );
};

export default function IconGrid(props: IconGridFragment) {
  const { headline, subhead, items, includeFooter, footer } = props;
  const showHeader = Boolean(headline || subhead);

  // -------------------- Animation

  const rootEl = useRef<HTMLDivElement>(null);
  const { isDesktop } = useViewSize();
  const REVEAL = AnimVals;
  const triggerPercent = isDesktop ? REVEAL.lg.triggerPercent : REVEAL.sm.triggerPercent;
  const isInView = useInView(rootEl, { amount: triggerPercent, once: REVEAL.once });
  const isRevealEnabled = true;

  // -------------------- Render

  return (
    <section className={cx(["icon-grid"])} ref={rootEl}>
      <RevealWrapper isInView={isInView}>
        <Container>
          { showHeader && (
            <SectionHeader
              headline={headline}
              subhead={subhead}
              isRevealEnabled={isRevealEnabled}
              isInView={isInView}
            />
          )}
          <div className={cx(["icon-grid__items"])}>
            {items?.map((item, index) => (
              <IconGridItem key={index} {...item} />
            ))}
          </div>
          { includeFooter && footer && (
            <SectionFooter
              text={footer.text}
              ctas={footer.ctas}
            />
          )}
        </Container>
      </RevealWrapper>
    </section>
  )
}

IconGrid.fragments = {
  entry: gql`
    fragment IconGrid on FlexiblePageContentContentIconGridLayout {
      headline
      subhead
      includeFooter
      footer {
        text
        ctas {
          linkType
          externalLink
          label
          page {
            node {
              uri
            }
          }
        }
      }
      items {
        icon {
          ...ACFMedia
        }
        headline
        body
        cta {
          linkType
          externalLink
          sectionId
          label
          page {
            node {
              uri
            }
          }
        }
      }
    }
  `,
};
